import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadIcon, SparklesIcon, DownloadIcon, CheckIcon, FileSpreadsheetIcon, DatabaseIcon } from 'lucide-react';
const tabs = [{
  id: 'upload',
  icon: UploadIcon,
  title: 'Find or upload local business records',
  description: 'Search for local businesses directly in Openmart, upload a CSV of your existing records, or connect your CRM. Our local business data enrichment engine prepares your records instantly.'
}, {
  id: 'enrich',
  icon: SparklesIcon,
  title: 'Enrich local business contacts',
  description: 'Automatically enrich each local business record with verified owner emails, direct phones, tech stacks, social profiles, revenue estimates, and 50+ data fields.'
}, {
  id: 'export',
  icon: DownloadIcon,
  title: 'Export or sync enriched data',
  description: 'Push enriched local business contact data directly to Salesforce, HubSpot, or download as CSV. Every record is structured, deduplicated, and ready for outreach.'
}];
export function EnrichmentWorkflow() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-4592">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-4593">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-4594">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-4595">
            How local business data{' '}
            <span className="text-blue-600" data-id="element-4596">enrichment</span> works
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-4597">
            From incomplete records to fully enriched local business profiles in
            minutes.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-4598">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-4599">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-4600">
                  <div className="flex items-start gap-6" data-id="element-4601">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-4602">
                      <tab.icon className="w-6 h-6" data-id="element-4603" />
                    </div>
                    <div data-id="element-4604">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-4605">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-4606">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-4607">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden" data-id="element-4608">
              <AnimatePresence mode="wait" data-id="element-4609">
                {activeTab === 'upload' && <motion.div key="upload" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md bg-white rounded-3xl p-8 shadow-sm border border-slate-100 text-center" data-id="element-4610">
                    <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-4611">
                      <FileSpreadsheetIcon className="w-8 h-8 text-blue-600" data-id="element-4612" />
                    </div>
                    <h4 className="font-bold text-slate-900 mb-2" data-id="element-4613">
                      Two ways to get started
                    </h4>
                    <p className="text-sm text-slate-500 mb-6" data-id="element-4614">
                      Choose how you want to add local business records
                    </p>

                    <div className="space-y-3 text-left" data-id="element-4615">
                      <div className="bg-slate-50 rounded-xl p-4 border-2 border-blue-100" data-id="element-4616">
                        <div className="flex items-center gap-3" data-id="element-4617">
                          <DatabaseIcon className="w-5 h-5 text-blue-600 flex-shrink-0" data-id="element-4618" />
                          <div data-id="element-4619">
                            <p className="text-sm font-semibold text-slate-900" data-id="element-4620">
                              Search in Openmart
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-4621">
                              Find local businesses by industry and location
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded-xl p-4 border border-slate-100" data-id="element-4622">
                        <div className="flex items-center gap-3" data-id="element-4623">
                          <FileSpreadsheetIcon className="w-5 h-5 text-slate-500 flex-shrink-0" data-id="element-4624" />
                          <div data-id="element-4625">
                            <p className="text-sm font-semibold text-slate-900" data-id="element-4626">
                              Upload a CSV
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-4627">
                              Import your existing local business records
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'enrich' && <motion.div key="enrich" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-xl" data-id="element-4628">
                    <div className="grid grid-cols-2 gap-4" data-id="element-4629">
                      {/* Before */}
                      <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 opacity-75" data-id="element-4630">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4" data-id="element-4631">
                          Input record
                        </div>
                        <div className="space-y-3" data-id="element-4632">
                          <div data-id="element-4633">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-4634">
                              Business name
                            </div>
                            <div className="text-sm font-medium text-slate-700" data-id="element-4635">
                              Smith Plumbing
                            </div>
                          </div>
                          <div data-id="element-4636">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-4637">
                              Address
                            </div>
                            <div className="text-sm font-medium text-slate-700" data-id="element-4638">
                              Austin, TX
                            </div>
                          </div>
                          <div className="pt-2 border-t border-slate-50" data-id="element-4639">
                            <div className="text-xs text-slate-400 italic" data-id="element-4640">
                              No email available
                            </div>
                            <div className="text-xs text-slate-400 italic mt-1" data-id="element-4641">
                              No owner info
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* After */}
                      <div className="bg-white rounded-2xl p-5 shadow-md border-2 border-blue-100 relative" data-id="element-4642">
                        <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center border-2 border-white z-10" data-id="element-4643">
                          <SparklesIcon className="w-3 h-3 text-white" data-id="element-4644" />
                        </div>
                        <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-4 flex items-center gap-1" data-id="element-4645">
                          <CheckIcon className="w-3 h-3" data-id="element-4646" /> Enriched profile
                        </div>
                        <div className="space-y-3" data-id="element-4647">
                          <div data-id="element-4648">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-4649">
                              Business name
                            </div>
                            <div className="text-sm font-medium text-slate-900" data-id="element-4650">
                              Smith Plumbing & HVAC LLC
                            </div>
                          </div>
                          <div data-id="element-4651">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-4652">
                              Owner contact
                            </div>
                            <div className="text-sm font-medium text-slate-900 flex items-center gap-1" data-id="element-4653">
                              <CheckIcon className="w-3 h-3 text-green-500" data-id="element-4654" />{' '}
                              John Smith
                            </div>
                          </div>
                          <div data-id="element-4655">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-4656">
                              Verified email
                            </div>
                            <div className="text-sm font-medium text-blue-600 flex items-center gap-1" data-id="element-4657">
                              <CheckIcon className="w-3 h-3 text-green-500" data-id="element-4658" />{' '}
                              john@smithplumbing.com
                            </div>
                          </div>
                          <div data-id="element-4659">
                            <div className="text-[10px] text-slate-400 uppercase" data-id="element-4660">
                              Direct phone
                            </div>
                            <div className="text-sm font-medium text-slate-900 flex items-center gap-1" data-id="element-4661">
                              <CheckIcon className="w-3 h-3 text-green-500" data-id="element-4662" />{' '}
                              (512) 555-0198
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'export' && <motion.div key="export" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md" data-id="element-4663">
                    <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100" data-id="element-4664">
                      <h4 className="font-bold text-slate-900 mb-6 text-center" data-id="element-4665">
                        Export enriched local business data
                      </h4>

                      <div className="space-y-3 mb-8" data-id="element-4666">
                        <div className="flex items-center justify-between p-4 rounded-xl border-2 border-blue-600 bg-blue-50 cursor-pointer" data-id="element-4667">
                          <div className="flex items-center gap-3" data-id="element-4668">
                            <DatabaseIcon className="w-5 h-5 text-blue-600" data-id="element-4669" />
                            <span className="font-semibold text-blue-900" data-id="element-4670">
                              Salesforce
                            </span>
                          </div>
                          <div className="w-4 h-4 rounded-full border-4 border-blue-600 bg-white" data-id="element-4671"></div>
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors" data-id="element-4672">
                          <div className="flex items-center gap-3" data-id="element-4673">
                            <DatabaseIcon className="w-5 h-5 text-slate-600" data-id="element-4674" />
                            <span className="font-medium text-slate-700" data-id="element-4675">
                              HubSpot
                            </span>
                          </div>
                          <div className="w-4 h-4 rounded-full border border-slate-300" data-id="element-4676"></div>
                        </div>
                        <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-slate-300 cursor-pointer transition-colors" data-id="element-4677">
                          <div className="flex items-center gap-3" data-id="element-4678">
                            <FileSpreadsheetIcon className="w-5 h-5 text-slate-600" data-id="element-4679" />
                            <span className="font-medium text-slate-700" data-id="element-4680">
                              Download CSV
                            </span>
                          </div>
                          <div className="w-4 h-4 rounded-full border border-slate-300" data-id="element-4681"></div>
                        </div>
                      </div>

                      <button className="w-full py-3.5 bg-slate-900 text-white font-bold rounded-xl hover:bg-slate-800 transition-colors flex items-center justify-center gap-2" data-id="element-4682">
                        <DownloadIcon className="w-4 h-4" data-id="element-4683" /> Sync 9,824 records
                      </button>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}